import React from "react";
import { Router, Route, Switch, Redirect } from "react-router-dom";

import Loading from "./components/Loading";
import Sidebar from "./components/Sidebar";
import Dashboard from "./views/Dashboard";
import Home from "./views/Home";
import LandingPage from "./views/LandingPage";
import Profile from "./views/Profile";
import ExternalApi from "./views/ExternalApi";
import Login from "./views/Login";
import Unauthorized from "./views/Unauthorized";
import V2CsvUpload from "./components/V2CsvUpload";
import { useAuth } from "./context/AuthContext";
import { UploadProvider } from "./context/UploadContext";
import history from "./utils/history";

// styles
import "./App.css";

// fontawesome
import initFontAwesome from "./utils/initFontAwesome";
import OrgManagement from "./views/OrgManagement";
import ReportsHome from "./views/Reports/Home";
import IngestionReview from "./views/IngestionReview";
import ActivityRequests from "./views/ActivityRequests";
import ReviewRequests from "./views/ReviewRequests";
import Feedback from "./views/Feedback";
initFontAwesome();

const PrivateRoute = ({ component: Component, ...rest }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center animated-gradient">
        <Loading />
      </div>
    );
  }

  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated ? <Component {...props} /> : <Redirect to="/login" />
      }
    />
  );
};

// Wrapper component for routes that need UploadProvider
const UploadRoutes = () => {
  const { getAccessToken, handleUnauthorized } = useAuth();

  return (
    <UploadProvider
      getAccessToken={getAccessToken}
      onUnauthorized={handleUnauthorized}
    >
      <Switch>
        <PrivateRoute path="/" exact component={LandingPage} />
        <PrivateRoute path="/profile" component={Profile} />
        <PrivateRoute path="/dashboard" component={Dashboard} />
        <PrivateRoute path="/org-management" component={OrgManagement} />
        <PrivateRoute path="/reports/home" component={ReportsHome} />
        <PrivateRoute path="/ingestion" component={IngestionReview} />
        <PrivateRoute path="/external-api" component={ExternalApi} />
        <PrivateRoute path="/upload" component={V2CsvUpload} />
        <PrivateRoute path="/activity-requests" component={ActivityRequests} />
        <PrivateRoute path="/review-requests" component={ReviewRequests} />
        <PrivateRoute path="/feedback" component={Feedback} />
        <Redirect to="/" />
      </Switch>
    </UploadProvider>
  );
};

const App = () => {
  const { isLoading, error, isAuthenticated, isUnauthorized } = useAuth();

  // Redirect to unauthorized page if user is not allowed
  if (isUnauthorized) {
    return (
      <Router history={history}>
        <Route component={Unauthorized} />
      </Router>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          background:
            "linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)",
        }}
      >
        <div className="glass-card p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Oops...</h2>
          <p className="text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center animated-gradient">
        <Loading />
      </div>
    );
  }

  return (
    <Router history={history}>
      <Switch>
        <Route path="/login" exact component={Login} />
        <Route path="/unauthorized" exact component={Unauthorized} />
        <>
          {isAuthenticated && <Sidebar />}
          <main
            className="flex-grow-1 grid-pattern"
            style={{
              marginLeft: isAuthenticated ? "224px" : "0",
              padding: "1.5rem",
              minHeight: "100vh",
            }}
          >
            <UploadRoutes />
          </main>
        </>
      </Switch>
    </Router>
  );
};

export default App;
